export const AU_STATES = [
  "ACT",
  "NSW",
  "NT",
  "QLD",
  "SA",
  "TAS",
  "VIC",
  "WA",
] as const;

/** Mapped to Address.AddressLine2 by AddressComponent.GetAddress. */
export const ADDRESS_LINE_2_COMPONENT_TYPE = "address_line_2";

export const MIN_ADDRESS_LINE_LENGTH = 6;
export const MIN_SUBURB_LENGTH = 3;
export const MIN_POSTCODE = 800;
export const MAX_POSTCODE = 9999;

const COUNTRY_NAMES: Record<string, string> = {
  AU: "Australia",
  NZ: "New Zealand",
  US: "United States",
  GB: "United Kingdom",
};

export interface ManualAddressParts {
  line1: string;
  line2: string;
  suburb: string;
  state: string;
  postcode: string;
  countryCode: string;
}

export interface ManualGeocodedAddress {
  address_components: Array<{
    long_name: string;
    short_name: string;
    types: string[];
  }>;
  formatted_address: string;
  geometry: { location: { lat: number; lng: number } };
  isManualEntry?: boolean;
}

const component = (name: string, types: string[]) => ({
  long_name: name,
  short_name: name,
  types,
});

export function normalizeCountryCode(country?: string | null): string {
  return (country || "au").trim().toUpperCase();
}

export function getCountryDisplayName(countryCode: string): string {
  const code = normalizeCountryCode(countryCode);
  return COUNTRY_NAMES[code] || code;
}

/** First non-empty line → line1; remaining lines joined into line2. */
export function splitAddressLines(addressText: string): {
  line1: string;
  line2: string;
} {
  const lines = addressText
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  return { line1: lines[0] || "", line2: lines.slice(1).join(", ") };
}

export function joinAddressLines(line1: string, line2: string): string {
  return [line1, line2]
    .map((p) => p.trim())
    .filter(Boolean)
    .join("\n");
}

export function hasAnyManualAddressInput(parts: ManualAddressParts): boolean {
  const { line1, line2, suburb, state, postcode } = parts;
  return [line1, line2, suburb, state, postcode].some((v) => v.trim());
}

export function isValidAustralianPostcode(postcode: string): boolean {
  const trimmed = postcode.trim();
  if (!/^\d{3,4}$/.test(trimmed)) return false;
  const value = Number.parseInt(trimmed, 10);
  return value >= MIN_POSTCODE && value <= MAX_POSTCODE;
}

export function sanitizePostcodeInput(value: string): string {
  return value.replace(/\D/g, "").slice(0, 4);
}

function requireMin(
  label: string,
  value: string,
  min: number,
  required: boolean
): string | null {
  if (!value) return required ? `${label} is required` : null;
  if (value.length < min) return `${label} must be at least ${min} characters`;
  return null;
}

/**
 * Empty address → ok. Any field populated → full address required.
 * Values are trimmed before checks.
 */
export function getManualAddressValidationError(
  parts: ManualAddressParts
): string | null {
  if (!hasAnyManualAddressInput(parts)) return null;

  const line1 = parts.line1.trim();
  const line2 = parts.line2.trim();
  const suburb = parts.suburb.trim();
  const state = parts.state.trim();
  const postcode = parts.postcode.trim();

  return (
    requireMin("Address line 1", line1, MIN_ADDRESS_LINE_LENGTH, true) ||
    requireMin("Address line 2", line2, MIN_ADDRESS_LINE_LENGTH, false) ||
    requireMin("Suburb", suburb, MIN_SUBURB_LENGTH, true) ||
    (!state ? "State is required" : null) ||
    (!postcode
      ? "Postcode is required"
      : !isValidAustralianPostcode(postcode)
      ? `Postcode must be 3–4 digits between ${MIN_POSTCODE} and ${MAX_POSTCODE}`
      : null) ||
    (!parts.countryCode.trim() ? "Country is required" : null)
  );
}

export function buildManualGeocodedAddress(
  parts: ManualAddressParts
): ManualGeocodedAddress {
  const countryCode = normalizeCountryCode(parts.countryCode);
  const countryName = getCountryDisplayName(countryCode);
  const line1 = parts.line1.trim();
  const line2 = parts.line2.trim();
  const suburb = parts.suburb.trim();
  const state = parts.state.trim();
  const postcode = parts.postcode.trim();

  return {
    address_components: [
      component(line1, ["route"]),
      ...(line2 ? [component(line2, [ADDRESS_LINE_2_COMPONENT_TYPE])] : []),
      component(suburb, ["locality", "political"]),
      component(state, ["administrative_area_level_1", "political"]),
      component(postcode, ["postal_code"]),
      {
        long_name: countryName,
        short_name: countryCode,
        types: ["country", "political"],
      },
    ],
    formatted_address: [line1, line2, suburb, state, postcode, countryName]
      .filter(Boolean)
      .join(", "),
    geometry: { location: { lat: 0, lng: 0 } },
    isManualEntry: true,
  };
}

function readCoord(
  value: number | (() => number) | undefined
): number | undefined {
  if (typeof value === "function") return value();
  return typeof value === "number" ? value : undefined;
}

export function isManualGeocodedAddress(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const address = value as ManualGeocodedAddress;
  if (address.isManualEntry) return true;
  const loc = address.geometry?.location;
  return readCoord(loc?.lat) === 0 && readCoord(loc?.lng) === 0;
}

export function parseManualPartsFromGeocodedAddress(
  value: ManualGeocodedAddress | null | undefined,
  fallbackCountryCode: string
): ManualAddressParts {
  const components = value?.address_components || [];
  const find = (type: string) => components.find((c) => c.types.includes(type));

  return {
    line1: find("route")?.long_name || "",
    line2: find(ADDRESS_LINE_2_COMPONENT_TYPE)?.long_name || "",
    suburb: find("locality")?.long_name || find("postal_town")?.long_name || "",
    state: find("administrative_area_level_1")?.short_name || "",
    postcode: find("postal_code")?.long_name || "",
    countryCode:
      find("country")?.short_name || normalizeCountryCode(fallbackCountryCode),
  };
}

export function hasCompleteManualParts(parts: ManualAddressParts): boolean {
  return (
    hasAnyManualAddressInput(parts) &&
    getManualAddressValidationError(parts) === null
  );
}

export function emptyManualAddressParts(
  countryCode: string
): ManualAddressParts {
  return {
    line1: "",
    line2: "",
    suburb: "",
    state: "",
    postcode: "",
    countryCode: normalizeCountryCode(countryCode),
  };
}
